import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Box,
  Grid,
  Stack,
  Tooltip,
  Typography,
} from "@mui/material";
import { ExpandMore as ExpandMoreIcon } from "@mui/icons-material";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import React from "react";

import InfoField from "./infoField";
import { incomingCallPopperSx } from "./overlayPolicy";

// Minimal vessel info matching CallData vessels structure
interface MinimalVesselInfo {
  vesselName?: string | null;
  voyage?: string | null;
  operator?: string | null;
  managementCompany?: string | null;
  agent?: string | null;
  secondaryAgent?: string | null;
  currentStatus?: string | null;
  currentBerth?: string | null;
  nextBerth?: string | null;
  eta?: string | null;
  etd?: string | null;
  appointedOperator?: boolean | null;
  appointedManagement?: boolean | null;
  assignedAgentId?: string | null;
  currentBerthId?: string | null;
  operatorContactName?: string | null;
  operatorContactPhone?: string | null;
  operatorContactEmail?: string | null;
}

interface VesselAccordionProps {
  vessels: MinimalVesselInfo[];
  defaultExpandedIndex?: number;
}

const VesselAccordion: React.FC<VesselAccordionProps> = ({
  vessels,
  defaultExpandedIndex = 0,
}) => {
  const [expanded, setExpanded] = React.useState<number | false>(
    vessels.length > 1 ? defaultExpandedIndex : 0
  );

  const handleChange = (index: number) => (_event: React.SyntheticEvent, newExpanded: boolean) => {
    setExpanded(newExpanded ? index : false);
  };

  if (!vessels || vessels.length === 0) {
    return (
      <Typography variant="body2" color="text.secondary" textAlign="center">
        No vessel data available
      </Typography>
    );
  }

  // If only one vessel, show it without accordion
  if (vessels.length === 1) {
    return <VesselDetails vessel={vessels[0]} />;
  }

  // Multiple vessels - show as accordion
  return (
    <Stack spacing={1}>
      {vessels.map((vessel, index) => (
        <Accordion
          key={index}
          expanded={expanded === index}
          onChange={handleChange(index)}
          sx={{
            border: "1px solid",
            borderColor: "divider",
            "&:before": {
              display: "none",
            },
          }}
        >
          <AccordionSummary expandIcon={<ExpandMoreIcon />}>
            <Stack spacing={0.5} sx={{ width: "100%" }}>
              <Typography variant="subtitle2" fontWeight={600}>
                {vessel.vesselName} - {vessel.voyage}
              </Typography>
              <Typography variant="caption" color="text.secondary">
                {vessel.currentBerth ? `Berth: ${vessel.currentBerth}` : "No current berth"}
              </Typography>
            </Stack>
          </AccordionSummary>
          <AccordionDetails>
            <VesselDetails vessel={vessel} />
          </AccordionDetails>
        </Accordion>
      ))}
    </Stack>
  );
};

interface VesselDetailsProps {
  vessel: MinimalVesselInfo;
}

const VesselDetails: React.FC<VesselDetailsProps> = ({ vessel: v }) => (
  <Grid container spacing={1}>
    <Grid item xs={6}>
      <InfoField label="Vessel" value={v.vesselName || "Unknown"} />
    </Grid>
    <Grid item xs={6}>
      <InfoField label="Voyage" value={v.voyage || "Unknown"} />
    </Grid>
    <Grid item xs={6}>
      <InfoField
        label="Operator"
        valueJsx={
          <Box>
            <Stack direction="row" alignItems="center" spacing={0.5}>
              <CheckCircleIcon
                sx={{
                  fontSize: "14px",
                  color: v.appointedOperator ? "success.main" : "grey.500",
                }}
              />
              <Typography
                variant="subtitle2"
                fontWeight={500}
                sx={{ overflowWrap: "break-word", whiteSpace: "normal" }}
              >
                {(v.operator || "Unknown").trim()}
                {v.operatorContactName ? "," : ""}
              </Typography>
            </Stack>
            {v.operatorContactName && (
              <Tooltip
                arrow
                slotProps={{ popper: { sx: incomingCallPopperSx } }}
                title={
                  <Box sx={{ p: 0.25 }}>
                    <Typography variant="caption" fontWeight={700} display="block">
                      {v.operatorContactName}
                    </Typography>
                    {v.operatorContactPhone && (
                      <Typography display="block" sx={{ color: "grey.300" }} variant="caption">
                        {v.operatorContactPhone.replace(/\s*ext\..*$/i, "")}
                      </Typography>
                    )}
                    {v.operatorContactEmail && (
                      <Typography display="block" sx={{ color: "grey.300" }} variant="caption">
                        {v.operatorContactEmail}
                      </Typography>
                    )}
                  </Box>
                }
              >
                <Typography
                  variant="subtitle2"
                  // color="text.secondary"
                  fontWeight={500}
                  sx={{ml:'20px', overflowWrap: "break-word", whiteSpace: "normal" }}
                >
                  {v.operatorContactName}
                </Typography>
              </Tooltip>
            )}
          </Box>
        }
      />
    </Grid>
    <Grid item xs={6}>
      <InfoField
        label="Management"
        valueJsx={
          <Stack direction="row" alignItems="center" spacing={0.5}>
            <CheckCircleIcon
              sx={{
                fontSize: "14px",
                color: v.appointedManagement ? "success.main" : "grey.500",
              }}
            />
            <Typography
              variant="subtitle2"
              overflow="hidden"
              textOverflow="ellipsis"
              whiteSpace="nowrap"
              fontWeight={500}
              title={v.managementCompany || "Unknown"}
            >
              {v.managementCompany || "Unknown"}
            </Typography>
          </Stack>
        }
      />
    </Grid>
    <Grid item xs={6}>
      <InfoField
        label="Current Berth"
        value={
          v.currentBerth
            ? v.currentStatus === "Shifting"
              ? `Shifting to ${v.nextBerth || "N/A"}`
              : v.currentBerth
            : "Upcoming"
        }
      />
    </Grid>
    <Grid item xs={6}>
      <InfoField
        label="Next Berth"
        value={
          v.nextBerth ??
          (v.currentBerth ? "Completed" : "Not Available")
        }
      />
    </Grid>
    <Grid item xs={6}>
      <InfoField label="Status" value={v.currentStatus || "N/A"} />
    </Grid>
    {v.agent && (
      <Grid item xs={6}>
        <InfoField label="Agent" value={v.agent} />
      </Grid>
    )}
    {v.secondaryAgent && (
      <Grid item xs={6}>
        <InfoField label="Backup" value={v.secondaryAgent} />
      </Grid>
    )}
  </Grid>
);

export default VesselAccordion;
